/**
 * ComplianceSummaryBar - Visual compliance progress indicator
 *
 * Displays a progress bar with percentage and issue alerts.
 * Used in VehicleDocumentsTab and other compliance-aware views.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  ShieldCheck,
  ShieldAlert
} from 'lucide-react';

/**
 * Get color classes based on completeness percentage
 */
function getComplianceColors(completeness) {
  if (completeness >= 100) {
    return {
      bar: 'bg-green-500',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: ShieldCheck,
      iconColor: 'text-green-600'
    };
  }
  if (completeness >= 80) {
    return {
      bar: 'bg-blue-500',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: ShieldCheck,
      iconColor: 'text-blue-600'
    };
  }
  if (completeness >= 50) {
    return {
      bar: 'bg-amber-500',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: ShieldAlert,
      iconColor: 'text-amber-600'
    };
  }
  return {
    bar: 'bg-red-500',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: ShieldAlert,
    iconColor: 'text-red-600'
  };
}

/**
 * Issue severity icon
 */
function IssueIcon({ severity }) {
  switch (severity) {
    case 'critical':
    case 'error':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'expiring':
      return <Clock className="w-4 h-4 text-orange-500" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-gray-500" />;
  }
}

/**
 * ComplianceSummaryBar
 *
 * @param {Object} props
 * @param {number} props.completeness - Percentage 0-100
 * @param {number} [props.requiredCount] - Total required folders
 * @param {number} [props.completeCount] - Completed required folders
 * @param {Array} [props.issues] - Array of {type, message, severity}
 * @param {boolean} [props.showDetails=true] - Show issue details
 * @param {string} [props.className] - Additional CSS classes
 */
function ComplianceSummaryBar({
  completeness = 0,
  requiredCount,
  completeCount,
  issues = [],
  showDetails = true,
  className = ''
}) {
  const { t } = useTranslation();
  const colors = getComplianceColors(completeness);
  const Icon = colors.icon;
  const percentage = Math.round(completeness);

  // Filter critical issues
  const criticalIssues = issues.filter(i =>
    i.severity === 'critical' || i.severity === 'error' || i.severity === 'warning' || i.severity === 'expiring'
  );

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4 ${className}`}>
      {/* Header with icon and percentage */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${colors.iconColor}`} />
          <span className={`font-semibold ${colors.text}`}>
            {t('common.docs.compliance.title')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {requiredCount !== undefined && completeCount !== undefined && (
            <span className="text-sm text-gray-600">
              {t('common.docs.compliance.requiredCount', { complete: completeCount, required: requiredCount })}
            </span>
          )}
          <span className={`font-bold text-lg ${colors.text}`}>
            {percentage}%
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full ${colors.bar} transition-all duration-500`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>

      {/* Issues list */}
      {showDetails && criticalIssues.length > 0 && (
        <div className="mt-3 space-y-1">
          {criticalIssues.slice(0, 3).map((issue, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm">
              <IssueIcon severity={issue.severity} />
              <span className="text-gray-700">{issue.message}</span>
            </div>
          ))}
          {criticalIssues.length > 3 && (
            <div className="text-sm text-gray-500 ml-6">
              {t('common.docs.compliance.moreAlerts', { count: criticalIssues.length - 3 })}
            </div>
          )}
        </div>
      )}

      {/* All complete message */}
      {criticalIssues.length === 0 && percentage >= 100 && (
        <div className="flex items-center gap-2 mt-2 text-sm text-green-700">
          <CheckCircle className="w-4 h-4" />
          <span>{t('common.docs.compliance.allComplete')}</span>
        </div>
      )}
    </div>
  );
}

export default ComplianceSummaryBar;
